import { defineStore } from 'pinia'
import type {
    AppraisalVehicleData,
    AppraisalVehicleValuePrice,
    KBBVehicle,
    KBBVehicleOption,
    KBBVehicleOptionCategory,
} from '~/utils/types/appraisal'
import { useFetchDealerMe } from '~/composables/fetch/useFetchDealerMe'
import { AppraisalSelectionType } from '~/utils/types/appraisal/enums'
import { ModalSize } from '~/utils/types/global/enums'

export const useAppraisalStore = defineStore('appraisal', {
    state: (): State => ({
        selectionType: null,
        allowedSelectionTypes: [
            AppraisalSelectionType.LICENSE_PLATE,
            AppraisalSelectionType.VIN,
            AppraisalSelectionType.MANUAL,
        ],
        appraisalVehicleData: {},
        vinSearchVehicles: [],
        manualSelectionStep: 1,
        manualSelectionCompleteStep: 5,
        manualQueryData: {},
        manualQueryLoading: false,
        testData: [
            {
                license_plate: 'BOA3029',
                license_state: 'WA',
                vin: '19UUB2F57FA017814',
            },
            {},
            {},
            {},
            {
                miles: 25000,
                postal_code: 98199,
            },
        ],
    }),
    getters: {
        kbbVehicle(state) {
            return state.appraisalVehicleData.kbbVehicle
        },
        vehicleOptionCategories(): KBBVehicleOptionCategory[] {
            return this.kbbVehicle?.vehicleOptionCategories ?? []
        },
        vehicleOptionsFormatted(): KBBVehicleOption[] {
            let returnArr: KBBVehicleOption[] = []

            this.vehicleOptionCategories.forEach(
                (category: KBBVehicleOptionCategory) => {
                    returnArr = [...returnArr, ...category.options]
                },
            )

            return returnArr
        },
        selectedVehicleOptionIds(): number[] {
            return this.appraisalVehicleData.optionIds ?? []
        },
        selectedVehicleOptions(): KBBVehicleOption[] {
            return this.vehicleOptionsFormatted.filter(
                (option: KBBVehicleOption) =>
                    this.selectedVehicleOptionIds.includes(
                        option.vehicleOptionId,
                    ),
            )
        },
        valuationPrices(): AppraisalVehicleValuePrice[] {
            return this.appraisalVehicleData.valueData?.prices ?? []
        },
        currentValuationPrice(): AppraisalVehicleValuePrice | null {
            return (
                this.valuationPrices.find(
                    (price: AppraisalVehicleValuePrice) =>
                        price.condition === this.appraisalVehicleData.condition,
                ) ?? null
            )
        },
        vehicleName(): string | null {
            return this.kbbVehicle
                ? `${this.kbbVehicle.yearId} ${this.kbbVehicle.makeName} ${this.kbbVehicle.modelPlusTrimName}`
                : null
        },
    },
    actions: {
        async getByVin(vin: string) {
            return await useFetchDealerMe(`/kbb/vin/${vin}`, {
                fetch: true,
            })
        },
        async getByLicensePlate(licensePlate: string, state: string) {
            return await useFetchDealerMe('/kbb/license-plate', {
                fetch: true,
                query: {
                    license_plate: licensePlate,
                    state,
                },
            })
        },
        async getVehicleOptions() {
            const { data } = await useFetchDealerMe(
                `/kbb/vehicles/${this.kbbVehicle?.vehicleId}/options`,
                {
                    fetch: true,
                },
            )

            if (this.appraisalVehicleData.kbbVehicle) {
                this.appraisalVehicleData.kbbVehicle.vehicleOptionCategories =
                    data.value.data
            }
        },
        async getVehicleValues() {
            const { data } = await useFetchDealerMe(
                `/kbb/vehicles/${this.kbbVehicle?.vehicleId}/values`,
                {
                    method: 'post',
                    body: {
                        optionIds: this.selectedVehicleOptionIds,
                        miles: this.appraisalVehicleData.miles,
                        postal_code: this.appraisalVehicleData.postal_code,
                    },
                    fetch: true,
                },
            )

            this.appraisalVehicleData.valueData = {
                prices: data.value.data,
                meta: data.value.meta,
            }
        },
        openModal() {
            useModalStore().openModal({
                component: 'AppraisalModal',
                size: ModalSize.MD,
            })
        },
        reset() {
            this.selectionType = null
            this.appraisalVehicleData = {}
            this.vinSearchVehicles = []
            this.manualSelectionStep = 1
            this.manualQueryData = {}

            this.resetAllowedSelectionTypes()
        },
        setKBBVehicle(vehicle: KBBVehicle) {
            this.appraisalVehicleData.kbbVehicle = vehicle
        },
        removeAllowedSelectionTypes(selectionTypes: AppraisalSelectionType[]) {
            selectionTypes.forEach((selectionType: AppraisalSelectionType) => {
                const index = this.allowedSelectionTypes.indexOf(selectionType)

                if (index > -1) {
                    this.allowedSelectionTypes.splice(index, 1)
                }
            })
        },
        resetAllowedSelectionTypes() {
            this.allowedSelectionTypes = [
                AppraisalSelectionType.LICENSE_PLATE,
                AppraisalSelectionType.VIN,
                AppraisalSelectionType.MANUAL,
            ]
        },
        resetVehicleSearches() {
            this.appraisalVehicleData.kbbVehicle = null
            this.vinSearchVehicles = []
        },
        async doManualQuery(field: string, query: Record<string, any> = {}) {
            this.manualQueryLoading = true

            const { data } = await useFetchDealerMe(`/kbb/${field}`, {
                fetch: true,
                query: { ...query, ...{ vehicleClass: 'UsedCar' } },
            })

            this.updateManualQueryData(field, data.value.data)
            this.manualQueryLoading = false
        },
        updateManualQueryData(field: string, data: any) {
            this.manualQueryData[field] = data
        },
    },
})

interface State {
    selectionType: AppraisalSelectionType | null
    allowedSelectionTypes: AppraisalSelectionType[]
    appraisalVehicleData: AppraisalVehicleData
    vinSearchVehicles: KBBVehicle[]
    manualSelectionStep: number
    manualSelectionCompleteStep: number
    manualQueryData: Record<string, any>
    manualQueryLoading: boolean
    testData: Record<string, any>[]
}
